import React, { useEffect, useCallback } from "react";
import {
  Link, useParams, useMatch, useNavigate,
} from "react-router";
import { useDispatch, useSelector } from "react-redux";
import type { Dispatch } from "redux";
import { LoadingButton } from "@fas/ui-core";
import { Box, Button } from "@mui/material";
import {
  savePageSaga,
  createPageSaga,
} from "../../actions/page";
import type { SavePageSagaOptions } from "../../actions/page";
import { getIsSavePageLoading } from "../../selectors/loading";
import { getHashValue } from "../../services/jusCan";

export default function ControlButtons() {
  const dispatch = useDispatch<Dispatch>();
  const isSavePageLoading = useSelector(getIsSavePageLoading);
  const { id: pageId } = useParams();
  const navigate = useNavigate();
  const isEditInfo = useMatch("/pages/editInfo/:pageId");

  const onSavePage = useCallback((options?: SavePageSagaOptions) => {
    dispatch(savePageSaga(options));
  }, [dispatch]);

  const onCreatePage = useCallback(() => {
    dispatch(createPageSaga(navigate));
  }, [dispatch]);

  const saveCallback = useCallback(
    (e: KeyboardEvent) => {
      if ((window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        onSavePage();
      }
    },
    [onSavePage]
  );

  useEffect(() => {
    window.addEventListener("keydown", saveCallback as EventListener, false);
    return () => {
      window.removeEventListener("keydown", saveCallback as EventListener, false);
    };
  }, [saveCallback]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        position: "fixed",
        bottom: "0px",
        right: "0px",
        left: "0px",
        background: "white",
        zIndex: 4,
      }}
      data-testid="action-container"
    >
      <Box>
        {!isEditInfo && pageId && (
          <Box sx={{ m: 1, display: "inline-block" }}>
            <Link
              to={`/pages/editInfo/${pageId}?hashId=${getHashValue()}`}
              data-testid="page-editinfo-btn"
            >
              <Button variant="outlined" color="info">General Info</Button>
            </Link>
          </Box>
        )}
        {pageId && (
          <>
            <LoadingButton
              sx={{ m: 1 }}
              data-testid="page-preview-btn"
              loading={isSavePageLoading}
              variant="outlined"
              color="secondary"
              onClick={() => onSavePage({ isPreviewEnabled: true })}
            >
              Preview
            </LoadingButton>

            <LoadingButton
              sx={{
                m: 1,
              }}
              data-testid="page-publish-btn"
              loading={isSavePageLoading}
              variant="contained"
              color="primary"
              onClick={() => onSavePage({ activateAfterSave: true })}
            >
              Activate
            </LoadingButton>

            <LoadingButton
              sx={{ m: 1 }}
              data-testid="page-save-btn"
              loading={isSavePageLoading}
              variant="contained"
              color="secondary"
              onClick={() => onSavePage()}
            >
              Save
            </LoadingButton>
          </>
        )}
        {!pageId && (
          <LoadingButton
            sx={{ m: 1 }}
            data-testid="page-create-btn"
            loading={isSavePageLoading}
            variant="contained"
            color="secondary"
            onClick={onCreatePage}
          >
            Create
          </LoadingButton>
        )}
      </Box>
    </Box>
  );
}
